import React from 'react';

import { Button } from 'components';
import { useTranslation } from 'react-i18next';

import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import { Channel } from 'lane-shared/types/ChannelType';
import {
  useBpMarkupChargeCodeEnabled,
  useBpAdminFeeChargeCodeEnabled,
} from 'react-hooks';

import { ErrorMessage } from 'components/general';

import { PageHeader, AdminPage } from 'components/layout';

import { BillingReport } from './BillingReport';
import { CurrencySetting } from './CurrencySetting';
import { Invoice } from './Invoice';
import { TaxInformation } from './TaxInformation';
import { Refund } from './Refund';

import styles from './styles.scss';

export function PaymentSettings({
  channel,
}: {
  channel: Pick<Channel, '_id'> | undefined;
}) {
  const { t } = useTranslation();
  const isBpMarkupChargeCodeEnabled = useBpMarkupChargeCodeEnabled();
  const isBpAdminFeeChargeCodeEnabled = useBpAdminFeeChargeCodeEnabled();

  const {
    paymentSettingsError,
    currency,
    updateCurrency,
    taxDetails,
    updateTaxDetails,
    invoiceDueInDays,
    updateInvoiceDueInDays,
    settingsUnchanged,
    updatePaymentSettings,
    invoiceDisplayName,
    updateInvoiceDisplayName,
    isInvoicingDisabled,
    updateIsInvoicingDisabled,
    failureRefund,
    cancellationRefund,
    updateFailureRefund,
    updateCancellationRefund,
    isReportMarkupEnabled,
    markupChargeCodeId,
    updateIsReportMarkupEnabled,
    updateMarkupChargeCodeId,
    isReportAdminFeeEnabled,
    adminFeeChargeCodeId,
    updateIsReportAdminFeeEnabled,
    updateAdminFeeChargeCodeId,
  } = useBillingPaymentSettingsForChannel({ channelId: channel?._id });

  const isTaxBusinessInfoIncomplete =
    !!taxDetails?.businessTaxName !== !!taxDetails?.businessTaxNumber;
  const isTaxDisplayInfoIncomplete =
    !!taxDetails?.taxDisplayName !== !!taxDetails?.taxNumber;
  const isMarkupEnabledWithoutChargeCode =
    isBpMarkupChargeCodeEnabled && isReportMarkupEnabled && !markupChargeCodeId;
  const isAdminFeeEnabledWithoutChargeCode =
    isBpAdminFeeChargeCodeEnabled &&
    isReportAdminFeeEnabled &&
    !adminFeeChargeCodeId;

  const disableSave =
    isTaxBusinessInfoIncomplete ||
    isTaxDisplayInfoIncomplete ||
    isMarkupEnabledWithoutChargeCode ||
    isAdminFeeEnabledWithoutChargeCode;

  async function updateWithToast() {
    const success = await updatePaymentSettings();

    if (success) {
      window.Toast.show(t('abp.billingAndPaymentSettings.updateSuccess'));
      window.location.reload();
    }
  }

  return (
    <AdminPage>
      <PageHeader
        externalPadding={[0, 0]}
        headerLevel="h3"
        header={t('abp.billingAndPaymentSettings.pageHeader')}
      />
      <div className={styles.settingsContainer}>
        <TaxInformation
          taxDetails={taxDetails}
          updateTaxDetails={updateTaxDetails}
        />
      </div>
      <div className={styles.settingsContainer}>
        <CurrencySetting currency={currency} updateCurrency={updateCurrency} />
      </div>

      {(isBpMarkupChargeCodeEnabled || isBpAdminFeeChargeCodeEnabled) && (
        <div className={styles.settingsContainer}>
          <BillingReport
            channelId={channel?._id}
            showMarkupToggle={isBpMarkupChargeCodeEnabled}
            isReportMarkupEnabled={isReportMarkupEnabled}
            markupChargeCodeId={markupChargeCodeId}
            updateIsReportMarkupEnabled={updateIsReportMarkupEnabled}
            updateMarkupChargeCodeId={updateMarkupChargeCodeId}
            showAdminFeeToggle={isBpAdminFeeChargeCodeEnabled}
            isReportAdminFeeEnabled={isReportAdminFeeEnabled}
            adminFeeChargeCodeId={adminFeeChargeCodeId}
            updateIsReportAdminFeeEnabled={updateIsReportAdminFeeEnabled}
            updateAdminFeeChargeCodeId={updateAdminFeeChargeCodeId}
          />
        </div>
      )}

      <div className={styles.settingsContainer}>
        <Invoice
          invoiceDueInDays={invoiceDueInDays}
          updateInvoiceDueInDays={updateInvoiceDueInDays}
          invoiceDisplayName={invoiceDisplayName}
          updateInvoiceDisplayName={updateInvoiceDisplayName}
          isInvoicingDisabled={isInvoicingDisabled}
          updateIsInvoicingDisabled={updateIsInvoicingDisabled}
        />
      </div>
      <div className={styles.settingsContainer}>
        <Refund
          failureRefund={failureRefund}
          cancellationRefund={cancellationRefund}
          updateFailureRefund={updateFailureRefund}
          updateCancellationRefund={updateCancellationRefund}
        />
      </div>
      <div className={styles.saveButton}>
        <Button
          disabled={settingsUnchanged || disableSave}
          variant="contained"
          onClick={() => updateWithToast()}
        >
          {t('abp.billingAndPaymentSettings.save')}
        </Button>
      </div>

      {paymentSettingsError && (
        <ErrorMessage error={t(paymentSettingsError.message)} />
      )}
    </AdminPage>
  );
}
